import { useEffect, useState } from "react";
import { FaEdit, FaTrash, FaDownload } from "react-icons/fa";
import {
  getReleves,
  createReleve,
  updateReleve,
  deleteReleve,
} from "../../services/releves";
import { getAllEntrepots } from "../../services/entrepots";
import { getAllChambresFroides } from "../../services/chambresfroides";
import { getAllGroupesFrigorifiques } from "../../services/groupesfrigorifiques";
import type { Releve } from "../../types/releve";
import type { Entrepot } from "../../types/entrepot";
import type { ChambreFroide } from "../../types/chambrefroide";
import type { GroupeFrigorifique } from "../../types/groupefrigorifique";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "../../assets/congelcam-logo.png";

import "./Releves.css";

export default function Releves() {
  const [releves, setReleves] = useState<Releve[]>([]);
  const [filteredReleves, setFilteredReleves] = useState<Releve[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedReleve, setSelectedReleve] = useState<Releve | null>(null);

  const [entrepots, setEntrepots] = useState<Entrepot[]>([]);
  const [allChambres, setAllChambres] = useState<ChambreFroide[]>([]);
  const [allGroupes, setAllGroupes] = useState<GroupeFrigorifique[]>([]);
  const [chambres, setChambres] = useState<ChambreFroide[]>([]);
  const [groupes, setGroupes] = useState<GroupeFrigorifique[]>([]);
  const [selectedEntrepot, setSelectedEntrepot] = useState<string>("");
  const [selectedChambre, setSelectedChambre] = useState<string>("");
  const [selectedGroupe, setSelectedGroupe] = useState<string>("");

  const [filters, setFilters] = useState({
    date_debut: "",
    date_fin: "",
    entrepot_nom: "",
    chambre_nom: "",
    groupe_nom: "",
  });

  useEffect(() => {
    const safeSetArray = <T,>(setter: React.Dispatch<React.SetStateAction<T[]>>) => (data: unknown) => {
      if (Array.isArray(data)) setter(data as T[]);
      else setter([]);
    };

    const fetchData = async () => {
      const data = await getReleves();
      safeSetArray<Releve>(setReleves)(data);
      safeSetArray<Releve>(setFilteredReleves)(data);

      const entrepotsData = await getAllEntrepots();
      safeSetArray<Entrepot>(setEntrepots)(entrepotsData);

      const chambresData = await getAllChambresFroides();
      safeSetArray<ChambreFroide>(setAllChambres)(chambresData);

      const groupesData = await getAllGroupesFrigorifiques();
      safeSetArray<GroupeFrigorifique>(setAllGroupes)(groupesData);
    };

    fetchData();
  }, []);


  const handleEntrepotChange = (id: string) => {
    setSelectedEntrepot(id);
    setSelectedChambre("");
    setSelectedGroupe("");
    setChambres(allChambres.filter(c => c.entrepot?.id === id));
    setGroupes([]);
  };

  const handleChambreChange = (id: string) => {
    setSelectedChambre(id);
    setSelectedGroupe("");
    setGroupes(allGroupes.filter(g => g.chambre_froide?.id === id));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const payload = {
      entrepot_id: selectedEntrepot,
      chambre_froide_id: selectedChambre,
      groupe_frigorifique_id: selectedGroupe,
      temperature: Number(formData.get("temperature")),
      pression_haute: Number(formData.get("pression_haute")),
      pression_basse: Number(formData.get("pression_basse")),
      courant_a1: Number(formData.get("courant_a1")),
      courant_a2: Number(formData.get("courant_a2")),
      courant_a3: Number(formData.get("courant_a3")),
      commentaire: formData.get("commentaire")?.toString() || null,
    };

    if (selectedReleve) {
      await updateReleve(selectedReleve.id, payload);
    } else {
      await createReleve(payload);
    }

    const data = await getReleves();
    setReleves(data);
    setFilteredReleves(data);
    setShowModal(false);
    setSelectedReleve(null);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Supprimer cette relève ?")) {
      await deleteReleve(id);
      setReleves(releves.filter(r => r.id !== id));
      setFilteredReleves(filteredReleves.filter(r => r.id !== id));
    }
  };

  // ✅ Export PDF simple
  const exportSinglePdf = (releve: Releve) => {
    const doc = new jsPDF();
    const img = new Image();
    img.src = logo;
    img.onload = () => {
      const imgWidth = img.width;
      const imgHeight = img.height;
      const targetWidth = 40; // largeur cible
      const targetHeight = (imgHeight / imgWidth) * targetWidth; // garder proportions
      const pageWidth = doc.internal.pageSize.getWidth();
      const x = (pageWidth - targetWidth) / 2;

      doc.addImage(img, "PNG", x, 10, targetWidth, targetHeight);

      doc.setFontSize(14);
      doc.text(
        `Relevé du ${releve.date} - ${releve.entrepot?.nom} / ${releve.chambre_froide?.nom} / ${releve.groupe_frigorifique?.nom}`,
        pageWidth / 2,
        40,
        { align: "center" }
      );

      autoTable(doc, {
        startY: targetHeight + 35,
        head: [["Date", "Heure", "Opérateur", "Température", "HP", "BP", "A1", "A2", "A3", "Commentaire"]],
        body: [[
          releve.date,
          releve.heure,
          `${releve.utilisateur?.first_name ?? ""} ${releve.utilisateur?.last_name ?? ""}`,
          releve.temperature ?? "",
          releve.pression_haute ?? "",
          releve.pression_basse ?? "",
          releve.courant_a1 ?? "",
          releve.courant_a2 ?? "",
          releve.courant_a3 ?? "",
          releve.commentaire ?? ""
        ]],
        styles: { fontSize: 9, halign: "center", valign: "middle" }, // ✅ police réduite et centrée
        headStyles: { fillColor: [37, 99, 235] }, // ✅ bleu pour l’en-tête
      });
      const filename = `releve-${releve.date}-${releve.entrepot?.nom}-${releve.chambre_froide?.nom}-${releve.groupe_frigorifique?.nom}.pdf`.replace(/ /g, "_");
      doc.save(filename);
    };
  };

  // ✅ Export PDF multiple
  const exportMultiplePdf = () => {
    const doc = new jsPDF();
    const img = new Image();
    img.src = logo;
    img.onload = () => {
      const imgWidth = img.width;
      const imgHeight = img.height;
      const targetWidth = 40; // largeur cible
      const targetHeight = (imgHeight / imgWidth) * targetWidth; // garder proportions
      const pageWidth = doc.internal.pageSize.getWidth();
      const x = (pageWidth - targetWidth) / 2;

      doc.addImage(img, "PNG", x, 10, targetWidth, targetHeight);

      doc.setFontSize(14);
      doc.text("Relèves Congelcam", pageWidth / 2, 40, { align: "center" });

      autoTable(doc, {
        startY: 50,
        head: [["Date", "Heure", "Entrepôt", "Chambre", "Groupe", "Opérateur", "Température", "HP", "BP", "A1", "A2", "A3", "Commentaire"]],
        body: filteredReleves.map(r => [
          r.date,
          r.heure,
          r.entrepot?.nom ?? "",
          r.chambre_froide?.nom ?? "",
          r.groupe_frigorifique?.nom ?? "",
          `${r.utilisateur?.first_name ?? ""} ${r.utilisateur?.last_name ?? ""}`,
          r.temperature ?? "",
          r.pression_haute ?? "",
          r.pression_basse ?? "",
          r.courant_a1 ?? "",
          r.courant_a2 ?? "",
          r.courant_a3 ?? "",
          r.commentaire ?? ""
        ]),
        styles: { fontSize: 9, halign: "center", valign: "middle" },
        headStyles: { fillColor: [37, 99, 235] },
      });

      const dateNow = new Date().toISOString().split("T")[0];
      const filename = `releves-${filters.date_debut || dateNow} au ${filters.date_fin || dateNow}.pdf`.replace(/ /g, "_");
      doc.save(filename);
    };
  };

    const handleSearch = () => {
    const filtered = releves.filter(r => {
      const matchEntrepot = filters.entrepot_nom
        ? r.entrepot?.nom.toLowerCase().includes(filters.entrepot_nom.toLowerCase())
        : true;
      const matchChambre = filters.chambre_nom
        ? r.chambre_froide?.nom.toLowerCase().includes(filters.chambre_nom.toLowerCase())
        : true;
      const matchGroupe = filters.groupe_nom
        ? r.groupe_frigorifique?.nom.toLowerCase().includes(filters.groupe_nom.toLowerCase())
        : true;
      const matchDateDebut = filters.date_debut ? r.date >= filters.date_debut : true;
      const matchDateFin = filters.date_fin ? r.date <= filters.date_fin : true;
      return matchEntrepot && matchChambre && matchGroupe && matchDateDebut && matchDateFin;
    });
    setFilteredReleves(filtered);
  };

  return (
    <div className="releves">
      <div className="releves-header">
        <button className="btn-ajout" onClick={() => setShowModal(true)}>
          + Nouvelle relève
        </button>
      </div>

      <div className="filters">
        <div className="filter-group">
          <label>Date début</label>
          <input
            type="date"
            value={filters.date_debut}
            onChange={(e) => setFilters({ ...filters, date_debut: e.target.value })}
          />
        </div>
        <div className="filter-group">
          <label>Date fin</label>
          <input
            type="date"
            value={filters.date_fin}
            onChange={(e) => setFilters({ ...filters, date_fin: e.target.value })}
          />
        </div>
        <div className="filter-group">
          <label>Nom entrepôt</label>
          <input
            type="text"
            value={filters.entrepot_nom}
            onChange={(e) => setFilters({ ...filters, entrepot_nom: e.target.value })}
          />
        </div>
        <div className="filter-group">
          <label>Nom chambre</label>
          <input
            type="text"
            value={filters.chambre_nom}
            onChange={(e) => setFilters({ ...filters, chambre_nom: e.target.value })}
          />
        </div>
        <div className="filter-group">
          <label>Nom groupe</label>
          <input
            type="text"
            value={filters.groupe_nom}
            onChange={(e) => setFilters({ ...filters, groupe_nom: e.target.value })}
          />
        </div>
        <button className="btn-search" onClick={handleSearch}>Rechercher</button>
        <button className="btn-export" onClick={exportMultiplePdf}>Exporter PDF</button>
      </div>

      <table className="releves-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Heure</th>
            <th>Opérateur</th>
            <th>Entrepôt</th>
            <th>Chambre</th>
            <th>Groupe</th>
            <th>Température</th>
            <th>HP</th>
            <th>BP</th>
            <th>A1</th>
            <th>A2</th>
            <th>A3</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredReleves.map((r) => (
            <tr key={r.id}>
              <td>{r.date}</td>
              <td>{r.heure}</td>
              <td>{r.utilisateur?.first_name} {r.utilisateur?.last_name}</td>
              <td>{r.entrepot?.nom}</td>
              <td>{r.chambre_froide?.nom}</td>
              <td>{r.groupe_frigorifique?.nom || "-"}</td>
              <td>{r.temperature}</td>
              <td>{r.pression_haute}</td>
              <td>{r.pression_basse}</td>
              <td>{r.courant_a1}</td>
              <td>{r.courant_a2}</td>
              <td>{r.courant_a3}</td>
              <td className="actions">
                <FaEdit
                  className="icon-edit"
                  onClick={() => {
                    setSelectedReleve(r);
                    setSelectedEntrepot(r.entrepot?.id || "");
                    setSelectedChambre(r.chambre_froide?.id || "");
                    setSelectedGroupe(r.groupe_frigorifique?.id || "");
                    setShowModal(true);
                  }}
                />
                <FaTrash
                  className="icon-trash"
                  onClick={() => handleDelete(r.id)}
                />
                <FaDownload
                  className="icon-download"
                  onClick={() => exportSinglePdf(r)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{selectedReleve ? "Modifier relève" : "Nouvelle relève"}</h3>
            <form onSubmit={handleSubmit}>
              {/* Sélection Entrepôt */}
              <select
                name="entrepot"
                value={selectedEntrepot}
                onChange={(e) => handleEntrepotChange(e.target.value)}
                required
              >
                <option value=""> -- Sélectionner un entrepôt -- </option>
                {entrepots.map((entrepot) => (
                  <option key={entrepot.id} value={entrepot.id}>
                    {entrepot.nom}
                  </option>
                ))}
              </select>

              {/* Sélection Chambre */}
              <select
                name="chambre"
                value={selectedChambre}
                onChange={(e) => handleChambreChange(e.target.value)}
                disabled={!selectedEntrepot}
                required
              >
                <option value=""> -- Sélectionner une chambre -- </option>
                {chambres.map((chambre) => (
                  <option key={chambre.id} value={chambre.id}>
                    {chambre.nom}
                  </option>
                ))}
              </select>

              {/* Sélection Groupe */}
              <select
                name="groupe"
                value={selectedGroupe}
                onChange={(e) => setSelectedGroupe(e.target.value)}
                disabled={!selectedChambre}
                required
              >
                <option value=""> -- Sélectionner un groupe -- </option>
                {groupes.map((groupe) => (
                  <option key={groupe.id} value={groupe.id}>
                    {groupe.nom}
                  </option>
                ))}
              </select>

              {/* Champs numériques activés seulement si groupe choisi */}
              <input
                type="number"
                name="temperature"
                placeholder="Température"
                defaultValue={selectedReleve?.temperature ?? ""}
                disabled={!selectedGroupe}
              />
              <input
                type="number"
                name="pression_haute"
                placeholder="HP"
                defaultValue={selectedReleve?.pression_haute ?? ""}
                disabled={!selectedGroupe}
              />
              <input
                type="number"
                name="pression_basse"
                placeholder="BP"
                defaultValue={selectedReleve?.pression_basse ?? ""}
                disabled={!selectedGroupe}
              />
              <input
                type="number"
                name="courant_a1"
                placeholder="A1"
                defaultValue={selectedReleve?.courant_a1 ?? ""}
                disabled={!selectedGroupe}
              />
              <input
                type="number"
                name="courant_a2"
                placeholder="A2"
                defaultValue={selectedReleve?.courant_a2 ?? ""}
                disabled={!selectedGroupe}
              />
              <input
                type="number"
                name="courant_a3"
                placeholder="A3"
                defaultValue={selectedReleve?.courant_a3 ?? ""}
                disabled={!selectedGroupe}
              />
              <textarea
                name="commentaire"
                placeholder="Commentaire (facultatif)"
                defaultValue={selectedReleve?.commentaire ?? ""}
                disabled={!selectedGroupe}
              />

              <div className="modal-actions">
                <button type="submit">Enregistrer</button>
                <button type="button" onClick={() => setShowModal(false)}>
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
